import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });

  const showError = (msg) => {
    setError(msg);
    toast({ title: 'Login failed', description: msg, variant: 'destructive' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, credentials, { timeout: 20000 });

      if (response?.data?.success && response.data.token) {
        localStorage.setItem('admin_token', response.data.token);
        toast({ title: 'Login successful', description: 'Welcome back!' });
        navigate('/admin/dashboard');
        return;
      }

      // Unexpected non-error response
      showError(response?.data?.detail || response?.data?.error || 'Unexpected response from server. Please try again.');
    } catch (err) {
      const status = err.response?.status;
      const backendMsg = err.response?.data?.detail || err.response?.data?.error;

      if (status === 401) {
        showError('Incorrect Admin ID or Access Code.');
      } else if (status === 429) {
        showError('Too many attempts. Wait a minute and try again.');
      } else if (err.code === 'ECONNABORTED') {
        showError('Server took too long to respond. Check your connection and try again.');
      } else if (!err.response) {
        showError('Cannot reach the server. Check your internet connection.');
      } else {
        showError(backendMsg || `Login failed (HTTP ${status || 'unknown'}). Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="container">
        <Card className="login-card">
          <CardHeader>
            <CardTitle className="text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_trustygas/artifacts/191wqlf3_file_000000004ca871f48b719ce9d2a32667.png" 
                alt="The Gas Guide"
                className="login-logo"
              />
              <h2>Admin Login</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="login-form">
              {error && (
                <div className="login-error-banner" data-testid="admin-login-error" role="alert">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
              <div className="form-field">
                <label>Admin ID</label>
                <Input
                  type="text"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  required
                  placeholder=""
                  autoComplete="off"
                  data-testid="admin-login-email"
                />
              </div>
              <div className="form-field">
                <label>Access Code</label>
                <Input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  placeholder=""
                  autoComplete="off"
                  data-testid="admin-login-password"
                />
              </div>
              <Button type="submit" className="btn-primary btn-full" disabled={loading} data-testid="admin-login-submit">
                {loading ? 'Authenticating...' : 'Access Dashboard'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
